import React, { useState } from 'react';
import { CompleteKundali, Language, ChartStyle, PlanetPosition, KundaliChartData } from '../types/kundali';
import { NorthIndianSvgChart, SouthIndianSvgChart, EastIndianSvgChart, ChartLegend } from './ChartViewer';
import { ResponsiveChartContainer } from './ResponsiveChartContainer';
import { ZODIAC_SIGNS } from '../utils/vedicEngine';
import { getPlanetShortCode, getZodiacName, getPlanetFullName, UI_TEXT } from '../utils/language';
import {
  ArrowLeftRight,
  Maximize2,
  Minimize2,
  Sparkles,
  Info,
  ChevronDown,
  Layers,
  CheckCircle2,
  Compass,
  Award,
  Activity
} from 'lucide-react';
import { GocharView } from './GocharView';
import { DashaChartView } from './DashaChartView';
import { AshtakvargaView } from './AshtakvargaView';
import { PlanetaryShadbalaMatrixView } from './PlanetaryShadbalaMatrixView';
import { UnifiedRemedialEngineView } from './UnifiedRemedialEngineView';

interface DualViewLayoutProps {
  kundali: CompleteKundali;
  chartStyle: ChartStyle;
  lang?: Language;
  showDegrees?: boolean;
  onSelectPlanet?: (p: PlanetPosition) => void;
  onSelectHouse?: (h: number) => void;
}

export const DualViewLayout: React.FC<DualViewLayoutProps> = ({
  kundali,
  chartStyle,
  lang = 'en',
  showDegrees = true,
  onSelectPlanet,
  onSelectHouse
}) => {
  const isHi = lang === 'hi';
  const ui = UI_TEXT[lang] || UI_TEXT.en;

  const [leftChartCode, setLeftChartCode] = useState<string>('D1');
  const [rightChartCode, setRightChartCode] = useState<string>('D9');
  const [selectedHouse, setSelectedHouse] = useState<number>(1);
  const [isCanvasExpanded, setIsCanvasExpanded] = useState<boolean>(false);
  const [highlightSynchronized, setHighlightSynchronized] = useState<boolean>(true);
  const [dualViewMode, setDualViewMode] = useState<'compare' | 'matrix' | 'remedies'>('compare');

  const divisionalOptions = [
    { code: 'D1', label: ui.D1 || 'D1 - Lagna Kundali' },
    { code: 'Chandra', label: ui.Chandra || 'Chandra Kundali' },
    { code: 'Surya', label: ui.Surya || 'Surya Kundali' },
    { code: 'Transit', label: ui.Transit || (isHi ? 'गोचर कुंडली' : 'Gochar Kundali') },
    { code: 'D9', label: ui.D9 || 'D9 - Navamsha Kundali' },
    { code: 'D10', label: ui.D10 || 'D10 - Dashamsha Kundali' },
    { code: 'D2', label: ui.D2 || 'D2 - Hora Kundali' },
    { code: 'D3', label: ui.D3 || 'D3 - Drekkana Kundali' },
    { code: 'D4', label: ui.D4 || 'D4 - Chaturthamsha Kundali' },
    { code: 'D7', label: ui.D7 || 'D7 - Saptamsha Kundali' },
    { code: 'D12', label: ui.D12 || 'D12 - Dwadashamsha Kundali' },
    { code: 'D16', label: ui.D16 || 'D16 - Shodashamsha Kundali' },
    { code: 'D20', label: ui.D20 || 'D20 - Vimshamsha Kundali' },
    { code: 'D24', label: ui.D24 || 'D24 - Chaturvimshamsha Kundali' },
    { code: 'D27', label: ui.D27 || 'D27 - Saptavimshamsha Kundali' },
    { code: 'D30', label: ui.D30 || 'D30 - Trimshamsha Kundali' },
    { code: 'D40', label: ui.D40 || 'D40 - Khavedamsha Kundali' },
    { code: 'D45', label: ui.D45 || 'D45 - Akshavedamsha Kundali' },
    { code: 'D60', label: ui.D60 || 'D60 - Shashtiamsha Kundali' },
    { code: 'DashaChart', label: isHi ? 'दशा कुंडली (महादशा)' : 'Dasha Kundali (Active Mahadasha)' }
  ];

  const presets = [
    { label: 'D1 + D9', left: 'D1', right: 'D9', desc: isHi ? 'लग्न व नवांश (भाग्य व विवाह)' : 'Lagna & Navamsha (Core & Destiny)' },
    { label: 'D1 + Transit', left: 'D1', right: 'Transit', desc: isHi ? 'लग्न व वर्तमान गोचर' : 'Lagna & Real-time Gochar' },
    { label: 'D1 + Chandra', left: 'D1', right: 'Chandra', desc: isHi ? 'लग्न व चंद्र कुंडली' : 'Lagna & Moon Chart (Mind)' },
    { label: 'D1 + D10', left: 'D1', right: 'D10', desc: isHi ? 'लग्न व दशमांश (करियर)' : 'Lagna & Dasamsha (Career)' },
    { label: 'D1 + D7', left: 'D1', right: 'D7', desc: isHi ? 'लग्न व सप्तमांश (संतान)' : 'Lagna & Saptamsha (Progeny)' },
    { label: 'D1 + DashaChart', left: 'D1', right: 'DashaChart', desc: isHi ? 'लग्न व दशा चक्र' : 'Lagna & Active Mahadasha' },
    { label: 'D9 + D10', left: 'D9', right: 'D10', desc: isHi ? 'नवांश व दशमांश' : 'Navamsha & Dasamsha' }
  ];

  const swapCharts = () => {
    const temp = leftChartCode;
    setLeftChartCode(rightChartCode);
    setRightChartCode(temp);
  };

  const getChartDataForCode = (code: string): KundaliChartData => {
    if (code === 'Transit') {
      const ascSign = kundali.planets?.find(p => p.name === 'Ascendant')?.signIndex || 0;
      return {
        divisionalCode: 'Transit',
        title: isHi ? 'गोचर चक्र (लग्न सापेक्ष)' : 'Gochar (Natal Ascendant Relative)',
        ascendantIndex: ascSign,
        houses: kundali.charts?.D1?.houses || kundali.houses || [],
        planets: kundali.charts?.D1?.planets || kundali.planets || []
      };
    }
    return (kundali.charts as any)?.[code] || kundali.charts?.D1 || {
      divisionalCode: 'D1',
      title: 'Lagna',
      ascendantIndex: 0,
      houses: kundali.houses || [],
      planets: kundali.planets || []
    };
  };

  const leftData = getChartDataForCode(leftChartCode);
  const rightData = getChartDataForCode(rightChartCode);

  // Compute Vargottama planets (occupying the same sign in Left & Right charts)
  const vargottamaPlanets = (kundali.planets || []).filter((p) => {
    const leftP = leftData.planets?.find((lp) => lp.name === p.name);
    const rightP = rightData.planets?.find((rp) => rp.name === p.name);
    if (!leftP || !rightP) return false;
    return leftP.signIndex === rightP.signIndex;
  });

  const renderSvgForChart = (chartData: KundaliChartData) => {
    if (chartStyle === 'south') {
      return (
        <SouthIndianSvgChart
          chartData={chartData}
          planets={chartData.planets}
          ashtakvarga={kundali.ashtakvarga}
          selectedHouseNumber={highlightSynchronized ? selectedHouse : undefined}
          onSelectHouse={(h) => {
            setSelectedHouse(h);
            onSelectHouse?.(h);
          }}
          onSelectPlanet={(p) => {
            if (p.house) setSelectedHouse(p.house);
            onSelectPlanet?.(p);
          }}
          lang={lang}
          showDegrees={showDegrees}
        />
      );
    }
    if (chartStyle === 'east') {
      return (
        <EastIndianSvgChart
          chartData={chartData}
          planets={chartData.planets}
          ashtakvarga={kundali.ashtakvarga}
          selectedHouseNumber={highlightSynchronized ? selectedHouse : undefined}
          onSelectHouse={(h) => {
            setSelectedHouse(h);
            onSelectHouse?.(h);
          }}
          onSelectPlanet={(p) => {
            if (p.house) setSelectedHouse(p.house);
            onSelectPlanet?.(p);
          }}
          lang={lang}
          showDegrees={showDegrees}
        />
      );
    }
    return (
      <NorthIndianSvgChart
        chartData={chartData}
        planets={chartData.planets}
        ashtakvarga={kundali.ashtakvarga}
        selectedHouseNumber={highlightSynchronized ? selectedHouse : undefined}
        onSelectHouse={(h) => {
          setSelectedHouse(h);
          onSelectHouse?.(h);
        }}
        onSelectPlanet={(p) => {
          if (p.house) setSelectedHouse(p.house);
          onSelectPlanet?.(p);
        }}
        lang={lang}
        showDegrees={showDegrees}
      />
    );
  };

  const leftSignNum = (((leftData?.ascendantIndex ?? 0) + (selectedHouse - 1)) % 12) + 1;
  const leftSignName = ZODIAC_SIGNS[leftSignNum - 1];
  const leftHousePlanets = leftData?.planets?.filter((p) => p.house === selectedHouse) || [];

  const rightSignNum = (((rightData?.ascendantIndex ?? 0) + (selectedHouse - 1)) % 12) + 1;
  const rightSignName = ZODIAC_SIGNS[rightSignNum - 1];
  const rightHousePlanets = rightData?.planets?.filter((p) => p.house === selectedHouse) || [];

  return (
    <div className={`w-full min-w-0 max-w-full space-y-6 transition-all duration-300 ${isCanvasExpanded ? 'max-w-none' : ''}`}>
      {/* 1. DUAL VIEW ACTION TOOLBAR & PRESETS */}
      <div className="bg-astro-bg border border-astro-border rounded-2xl p-4 sm:p-5 shadow-sm space-y-4">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 border-b border-astro-border pb-4">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-astro-primary text-white flex items-center justify-center shadow-xs">
              <ArrowLeftRight className="w-4 h-4 text-astro-accent" />
            </div>
            <div>
              <h3 className="font-bold font-serif text-astro-primary text-base sm:text-lg">
                {isHi ? 'तुलनात्मक दोहरा चार्ट विश्लेषण' : 'Dual Chart Side-by-Side Comparison'}
              </h3>
              <p className="text-xs text-astro-secondary-text">
                {isHi
                  ? 'दो कुंडलियों का एक साथ तुलनात्मक अध्ययन (उदा. D1 लग्न और D9 नवांश)'
                  : 'Compare any two divisional or transit charts simultaneously with synchronized house inspection'}
              </p>
            </div>
          </div>

          {/* Quick Toolbar Controls */}
          <div className="flex items-center gap-2 flex-wrap w-full md:w-auto justify-end">
            <button
              onClick={swapCharts}
              className="px-3 py-1.5 rounded-xl bg-astro-card hover:bg-astro-border border border-astro-border text-astro-primary text-xs font-bold flex items-center gap-1.5 shadow-2xs transition-all active:scale-95"
              title={isHi ? 'चार्ट बदलें (बायाँ ⇄ दायाँ)' : 'Swap Left & Right Charts'}
            >
              <ArrowLeftRight className="w-3.5 h-3.5" />
              <span>{isHi ? 'अदला-बदली (Swap)' : 'Swap'}</span>
            </button>

            <button
              onClick={() => setHighlightSynchronized(!highlightSynchronized)}
              className={`px-3 py-1.5 rounded-xl border text-xs font-semibold flex items-center gap-1.5 transition-all ${
                highlightSynchronized
                  ? 'bg-astro-primary/10 text-astro-primary border-astro-primary/30 font-bold'
                  : 'bg-astro-card text-astro-secondary-text border-astro-border'
              }`}
              title={isHi ? 'दोनों चार्ट में एक साथ भाव हाइलाइट करें' : 'Synchronize house highlight across both charts'}
            >
              <Layers className="w-3.5 h-3.5" />
              <span>{isHi ? 'भाव सिंक्रनाइज़' : 'Sync Focus'}</span>
            </button>

            <button
              onClick={() => setIsCanvasExpanded(!isCanvasExpanded)}
              className={`px-3 py-1.5 rounded-xl border text-xs font-semibold flex items-center gap-1.5 transition-all ${
                isCanvasExpanded
                  ? 'bg-astro-accent text-white border-astro-accent font-bold shadow-xs'
                  : 'bg-astro-card text-astro-secondary-text border-astro-border hover:text-astro-text'
              }`}
              title={isCanvasExpanded ? (isHi ? 'सामान्य आकार' : 'Standard View') : (isHi ? 'पूर्ण चौड़ाई विस्तार' : 'Maximize Chart Area')}
            >
              {isCanvasExpanded ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
              <span>{isCanvasExpanded ? (isHi ? 'संक्षिप्त' : 'Standard') : (isHi ? 'विशाल कैनवास' : 'Maximize')}</span>
            </button>
          </div>
        </div>

        {/* Quick Dual Comparison Presets */}
        <div className="flex items-center gap-2 overflow-x-auto pb-1 no-scrollbar text-xs">
          <span className="text-[11px] font-bold uppercase tracking-wider text-astro-secondary-text shrink-0">
            {isHi ? 'त्वरित तुलना:' : 'Quick Presets:'}
          </span>
          {presets.map((p) => {
            const isSelected = leftChartCode === p.left && rightChartCode === p.right;
            return (
              <button
                key={p.label}
                onClick={() => {
                  setLeftChartCode(p.left);
                  setRightChartCode(p.right);
                }}
                className={`px-3 py-1.5 rounded-xl border whitespace-nowrap font-bold transition-all shrink-0 ${
                  isSelected
                    ? 'bg-astro-primary text-white border-astro-primary shadow-xs'
                    : 'bg-astro-card text-astro-secondary-text border-astro-border hover:border-astro-primary/40 hover:text-astro-text'
                }`}
                title={p.desc}
              >
                {p.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* 2. RESPONSIVE 2-COLUMN GRID FOR DUAL CHARTS */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6 w-full min-w-0">
        {/* LEFT CHART CARD */}
        <div className="w-full min-w-0 h-full bg-astro-bg border border-astro-border rounded-2xl p-4 sm:p-5 shadow-sm flex flex-col justify-between items-center">
          {/* Left Chart Header & Selector */}
          <div className="w-full flex items-center justify-between gap-3 border-b border-astro-border pb-3 mb-3">
            <div className="flex items-center gap-2 min-w-0">
              <span className="w-3 h-3 rounded-full bg-astro-primary shrink-0" />
              <div className="relative">
                <select
                  value={leftChartCode}
                  onChange={(e) => setLeftChartCode(e.target.value)}
                  className="appearance-none font-bold text-xs sm:text-sm text-astro-primary font-serif bg-transparent pr-6 py-1 cursor-pointer focus:outline-none focus:ring-1 focus:ring-astro-primary rounded-lg"
                >
                  {divisionalOptions.map((opt) => (
                    <option key={opt.code} value={opt.code} className="font-sans text-xs">
                      {opt.label}
                    </option>
                  ))}
                </select>
                <ChevronDown className="w-3.5 h-3.5 text-astro-primary absolute right-0.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              </div>
            </div>

            <span className="text-[11px] font-mono font-bold px-2.5 py-0.5 rounded-full bg-astro-primary/10 text-astro-primary border border-astro-primary/20 shrink-0">
              {leftChartCode}
            </span>
          </div>

          {/* Left Fluid SVG Chart */}
          <ResponsiveChartContainer maxDimension={isCanvasExpanded ? 880 : 640} minDimension={220} className="w-full min-w-0 my-1">
            {() => (
              leftChartCode === 'DashaChart' ? (
                <DashaChartView
                  kundali={kundali}
                  chartStyle={chartStyle}
                  lang={lang}
                  showDegrees={showDegrees}
                  onSelectHouse={(h) => setSelectedHouse(h)}
                  onSelectPlanet={onSelectPlanet}
                />
              ) : (
                renderSvgForChart(leftData)
              )
            )}
          </ResponsiveChartContainer>

          {/* Left Chart Lagna & Details */}
          <div className="w-full flex items-center justify-between text-xs text-astro-secondary-text pt-2 border-t border-astro-border/80 mt-2">
            <span>
              {isHi ? 'लग्न राशि:' : 'Ascendant:'}{' '}
              <strong className="text-astro-primary">{getZodiacName(ZODIAC_SIGNS[leftData?.ascendantIndex ?? 0], lang)}</strong>
            </span>
            <span>
              {isHi ? 'चयनित भाव:' : 'Active House:'}{' '}
              <strong className="text-astro-accent">{isHi ? `भाव ${selectedHouse}` : `House ${selectedHouse}`}</strong>
            </span>
          </div>
        </div>

        {/* RIGHT CHART CARD */}
        <div className="w-full min-w-0 h-full bg-astro-bg border border-astro-border rounded-2xl p-4 sm:p-5 shadow-sm flex flex-col justify-between items-center">
          {/* Right Chart Header & Selector */}
          <div className="w-full flex items-center justify-between gap-3 border-b border-astro-border pb-3 mb-3">
            <div className="flex items-center gap-2 min-w-0">
              <span className="w-3 h-3 rounded-full bg-astro-accent shrink-0" />
              <div className="relative">
                <select
                  value={rightChartCode}
                  onChange={(e) => setRightChartCode(e.target.value)}
                  className="appearance-none font-bold text-xs sm:text-sm text-astro-primary font-serif bg-transparent pr-6 py-1 cursor-pointer focus:outline-none focus:ring-1 focus:ring-astro-primary rounded-lg"
                >
                  {divisionalOptions.map((opt) => (
                    <option key={opt.code} value={opt.code} className="font-sans text-xs">
                      {opt.label}
                    </option>
                  ))}
                </select>
                <ChevronDown className="w-3.5 h-3.5 text-astro-primary absolute right-0.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              </div>
            </div>

            <span className="text-[11px] font-mono font-bold px-2.5 py-0.5 rounded-full bg-astro-accent/15 text-astro-accent border border-astro-accent/30 shrink-0">
              {rightChartCode}
            </span>
          </div>

          {/* Right Fluid SVG Chart */}
          <ResponsiveChartContainer maxDimension={isCanvasExpanded ? 880 : 640} minDimension={220} className="w-full min-w-0 my-1">
            {() => (
              rightChartCode === 'DashaChart' ? (
                <DashaChartView
                  kundali={kundali}
                  chartStyle={chartStyle}
                  lang={lang}
                  showDegrees={showDegrees}
                  onSelectHouse={(h) => setSelectedHouse(h)}
                  onSelectPlanet={onSelectPlanet}
                />
              ) : (
                renderSvgForChart(rightData)
              )
            )}
          </ResponsiveChartContainer>

          {/* Right Chart Lagna & Details */}
          <div className="w-full flex items-center justify-between text-xs text-astro-secondary-text pt-2 border-t border-astro-border/80 mt-2">
            <span>
              {isHi ? 'लग्न राशि:' : 'Ascendant:'}{' '}
              <strong className="text-astro-primary">{getZodiacName(ZODIAC_SIGNS[rightData?.ascendantIndex ?? 0], lang)}</strong>
            </span>
            <span>
              {isHi ? 'चयनित भाव:' : 'Active House:'}{' '}
              <strong className="text-astro-accent">{isHi ? `भाव ${selectedHouse}` : `House ${selectedHouse}`}</strong>
            </span>
          </div>
        </div>
      </div>

      {/* 3. VARGOTTAMA PLANETS & DUAL HOUSE COMPARATIVE MATRIX */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6 items-start">
        {/* Left: Vargottama Planets Highlights */}
        <div className="lg:col-span-5 self-start lg:sticky lg:top-4 bg-astro-bg border border-astro-border rounded-2xl p-4 sm:p-5 shadow-sm space-y-3">
          <div className="flex items-center gap-2 text-astro-primary">
            <Award className="w-4 h-4 text-astro-accent" />
            <h4 className="font-bold font-serif text-sm sm:text-base">
              {isHi ? 'वर्गोत्तम ग्रह विश्लेषण (Vargottama Strength)' : 'Vargottama Planetary Strength'}
            </h4>
          </div>

          <p className="text-xs text-astro-secondary-text leading-relaxed">
            {isHi
              ? 'जब कोई ग्रह दोनों कुंडलियों में एक ही राशि में स्थित होता है, तो वह "वर्गोत्तम" होकर अत्यधिक शुभ और शक्तिशाली फल देता है।'
              : 'When a planet occupies the exact same zodiac sign in both compared charts, it attains Vargottama status, granting extraordinary auspicious vitality.'}
          </p>

          {vargottamaPlanets.length > 0 ? (
            <div className="space-y-2 pt-2">
              {vargottamaPlanets.map((vp) => (
                <div
                  key={vp.name}
                  className="p-2.5 rounded-xl bg-astro-card border border-astro-accent/40 flex items-center justify-between shadow-2xs"
                >
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-emerald-600" />
                    <span className="text-xs font-bold text-astro-primary">
                      {getPlanetFullName(vp.name, lang)}
                    </span>
                    <span className="text-[10px] font-mono px-2 py-0.5 rounded-md bg-astro-accent/15 text-astro-accent font-bold">
                      {isHi ? 'वर्गोत्तम' : 'Vargottama'}
                    </span>
                  </div>
                  <span className="text-xs font-medium text-astro-text">
                    {getZodiacName(ZODIAC_SIGNS[vp.signIndex], lang)} ({vp.degreeInSign.toFixed(1)}°)
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-3 rounded-xl bg-astro-card border border-astro-border text-xs text-astro-secondary-text text-center">
              {isHi
                ? `${leftChartCode} और ${rightChartCode} में कोई ग्रह एक ही राशि में नहीं है।`
                : `No identical sign placements between ${leftChartCode} and ${rightChartCode}.`}
            </div>
          )}
        </div>

        {/* Right: Comparative House Inspector (House 1 to 12) */}
        <div className="lg:col-span-7 bg-astro-bg border border-astro-border rounded-2xl p-4 sm:p-5 shadow-sm space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2 border-b border-astro-border pb-3">
            <div className="flex items-center gap-2">
              <Compass className="w-4 h-4 text-astro-primary" />
              <h4 className="font-bold font-serif text-sm sm:text-base text-astro-primary">
                {isHi ? `भाव ${selectedHouse} का दोहरा विश्लेषण` : `House ${selectedHouse} Dual Comparison`}
              </h4>
            </div>

            {/* View Mode Toggle */}
            <div className="flex items-center gap-1 bg-astro-card p-1 rounded-xl border border-astro-border">
              <button
                type="button"
                onClick={() => setDualViewMode('compare')}
                className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-all flex items-center gap-1 cursor-pointer ${
                  dualViewMode === 'compare'
                    ? 'bg-astro-primary text-white shadow-2xs'
                    : 'text-astro-secondary-text hover:text-astro-text'
                }`}
              >
                <Compass className="w-3.5 h-3.5" />
                <span>{isHi ? 'तुलना' : 'Compare'}</span>
              </button>
              <button
                type="button"
                onClick={() => setDualViewMode('matrix')}
                className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-all flex items-center gap-1 cursor-pointer ${
                  dualViewMode === 'matrix'
                    ? 'bg-astro-primary text-white shadow-2xs'
                    : 'text-astro-secondary-text hover:text-astro-text'
                }`}
              >
                <Activity className="w-3.5 h-3.5" />
                <span>{isHi ? 'मैट्रिक्स' : 'Matrix'}</span>
              </button>
              <button
                type="button"
                onClick={() => setDualViewMode('remedies')}
                className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-all flex items-center gap-1 cursor-pointer ${
                  dualViewMode === 'remedies'
                    ? 'bg-astro-primary text-white shadow-2xs'
                    : 'text-astro-secondary-text hover:text-astro-text'
                }`}
              >
                <Sparkles className="w-3.5 h-3.5" />
                <span>{isHi ? 'उपाय' : 'Upaya'}</span>
              </button>
            </div>

            {/* House Selector Pill Bar */}
            <div className="flex items-center gap-1 overflow-x-auto no-scrollbar py-0.5">
              {Array.from({ length: 12 }, (_, i) => i + 1).map((h) => (
                <button
                  key={h}
                  onClick={() => setSelectedHouse(h)}
                  className={`w-6 h-6 rounded-lg text-xs font-bold flex items-center justify-center transition-all ${
                    selectedHouse === h
                      ? 'bg-astro-primary text-white shadow-2xs'
                      : 'bg-astro-card text-astro-secondary-text hover:bg-astro-border'
                  }`}
                >
                  {h}
                </button>
              ))}
            </div>
          </div>

          {dualViewMode === 'matrix' && (
            <PlanetaryShadbalaMatrixView
              kundali={kundali}
              chartData={leftData}
              selectedHouseNumber={selectedHouse}
              lang={lang}
              onSelectPlanet={onSelectPlanet}
            />
          )}

          {dualViewMode === 'remedies' && (
            <UnifiedRemedialEngineView
              kundali={kundali}
              chartData={leftData}
              selectedHouseNumber={selectedHouse}
              lang={lang}
            />
          )}

          {dualViewMode === 'compare' && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Left Chart House Details */}
                <div className="p-3.5 rounded-xl bg-astro-card border border-astro-border space-y-2">
                  <div className="flex items-center justify-between text-xs font-bold text-astro-primary">
                    <span>{leftChartCode} ({leftData.title})</span>
                    <span className="font-serif font-bold text-astro-accent">
                      {getZodiacName(leftSignName, lang)} (R{leftSignNum})
                    </span>
                  </div>
                  <div className="text-[11px] text-astro-secondary-text">
                    {isHi ? 'स्थित ग्रह:' : 'Occupying Planets:'}{' '}
                    {leftHousePlanets.length > 0 ? (
                      <span className="font-bold text-astro-text">
                        {leftHousePlanets.map((p) => getPlanetFullName(p.name, lang)).join(', ')}
                      </span>
                    ) : (
                      <span className="italic text-astro-secondary-text">{isHi ? 'कोई ग्रह नहीं (खाली)' : 'None (Empty House)'}</span>
                    )}
                  </div>
                </div>

                {/* Right Chart House Details */}
                <div className="p-3.5 rounded-xl bg-astro-card border border-astro-border space-y-2">
                  <div className="flex items-center justify-between text-xs font-bold text-astro-primary">
                    <span>{rightChartCode} ({rightData.title})</span>
                    <span className="font-serif font-bold text-astro-accent">
                      {getZodiacName(rightSignName, lang)} (R{rightSignNum})
                    </span>
                  </div>
                  <div className="text-[11px] text-astro-secondary-text">
                    {isHi ? 'स्थित ग्रह:' : 'Occupying Planets:'}{' '}
                    {rightHousePlanets.length > 0 ? (
                      <span className="font-bold text-astro-text">
                        {rightHousePlanets.map((p) => getPlanetFullName(p.name, lang)).join(', ')}
                      </span>
                    ) : (
                      <span className="italic text-astro-secondary-text">{isHi ? 'कोई ग्रह नहीं (खाली)' : 'None (Empty House)'}</span>
                    )}
                  </div>
                </div>
              </div>

              <p className="text-xs text-astro-secondary-text flex items-center gap-1.5 pt-1">
                <Info className="w-3.5 h-3.5 text-astro-primary shrink-0" />
                <span>
                  {isHi
                    ? 'किसी भी भाव पर क्लिक करके दोनों कुंडलियों में उस भाव के प्रभाव एवं स्वामियों की तुलना करें।'
                    : 'Click any house number above or click on chart polygons to inspect comparative planetary dynamics.'}
                </span>
              </p>
            </>
          )}
        </div>
      </div>

      {/* Bottom Chart Legend */}
      <ChartLegend lang={lang} />
    </div>
  );
};
